/**
 * Custom Google Maps heatmap overlay backed by a 2D canvas.
 *
 * Why this exists
 * ---------------
 * Google deprecated `google.maps.visualization.HeatmapLayer` in May 2025
 * and announced removal in the May 2026 release. The official migration
 * path is either deck.gl (`@deck.gl/aggregation-layers`) or a custom
 * overlay. We chose the latter to avoid adding ~700KB of dependencies
 * for a single dashboard panel.
 *
 * The implementation mirrors the well-known "simpleheat" approach used
 * by Leaflet.heat / heatmap.js:
 *   1. For every point, draw a radial gradient (white → transparent)
 *      onto an offscreen canvas with `globalCompositeOperation = 'lighter'`
 *      so overlapping blobs accumulate alpha additively.
 *   2. Read back the canvas's alpha channel and translate each alpha
 *      value into a color via a precomputed palette LUT — that gives
 *      the familiar blue → cyan → green → yellow → red ramp.
 *
 * Because the overlay extends `google.maps.OverlayView` it is reprojected
 * automatically on every pan / zoom / resize via `draw()`, exactly the
 * same lifecycle the legacy HeatmapLayer used.
 *
 * Caller contract
 * ---------------
 *   const overlay = createHeatmapOverlay(maps, { radius: 40, opacity: 0.75 });
 *   overlay.setMap(map);            // attach
 *   overlay.setData(points, { maxIntensity });  // (re)render
 *   overlay.setMap(null);           // detach + dispose
 *
 * Points use the same `{ location: LatLng, weight: number }` shape as
 * the deprecated HeatmapLayer for drop-in compatibility, but plain
 * `{ lat, lng, weight }` works too.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface HeatmapPoint {
  lat?: number;
  lng?: number;
  location?: { lat(): number; lng(): number } | { lat: number; lng: number };
  weight?: number;
}

export interface HeatmapOverlayOptions {
  /** Radius of each heat blob in pixels (defaults to 40). */
  radius?: number;
  /** Final opacity of the colorized canvas (0–1). */
  opacity?: number;
  /**
   * Weight value that maps to the brightest end of the palette.
   * Anything above is clamped. Defaults to 1.
   */
  maxIntensity?: number;
  /** Optional palette ramp; defaults to blue→red heatmap colors. */
  gradient?: Array<[number, string]>;
}

export interface HeatmapOverlay {
  setMap(map: any | null): void;
  setData(points: HeatmapPoint[], opts?: { maxIntensity?: number; radius?: number }): void;
  setOptions(opts: Partial<HeatmapOverlayOptions>): void;
  redraw(): void;
}

const DEFAULT_GRADIENT: Array<[number, string]> = [
  [0.0, 'rgba(0, 0, 255, 0)'],
  [0.2, 'rgba(0, 0, 255, 0.85)'],
  [0.4, 'rgba(0, 255, 255, 1)'],
  [0.6, 'rgba(0, 255, 0, 1)'],
  [0.8, 'rgba(255, 255, 0, 1)'],
  [1.0, 'rgba(255, 0, 0, 1)'],
];

/**
 * Build a 256-entry RGBA palette LUT from `gradient` color stops.
 * The output is indexed by alpha 0–255 and produces RGB (alpha is
 * preserved from the source canvas, which is what the gradient blob
 * pass already encoded).
 */
const buildPalette = (gradient: Array<[number, string]>): Uint8ClampedArray => {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (!ctx) return new Uint8ClampedArray(256 * 4);
  const grad = ctx.createLinearGradient(0, 0, 0, 256);
  for (const [stop, color] of gradient) {
    grad.addColorStop(stop, color);
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 1, 256);
  return ctx.getImageData(0, 0, 1, 256).data;
};

const resolveLatLng = (p: HeatmapPoint): { lat: number; lng: number } | null => {
  if (typeof p.lat === 'number' && typeof p.lng === 'number') {
    return { lat: p.lat, lng: p.lng };
  }
  const loc = p.location as any;
  if (!loc) return null;
  if (typeof loc.lat === 'function' && typeof loc.lng === 'function') {
    return { lat: loc.lat(), lng: loc.lng() };
  }
  if (typeof loc.lat === 'number' && typeof loc.lng === 'number') {
    return { lat: loc.lat, lng: loc.lng };
  }
  return null;
};

/**
 * Factory — `maps` must be the loaded `google.maps` namespace, since
 * `OverlayView` is only defined after the Maps script finishes loading.
 */
export const createHeatmapOverlay = (
  maps: any,
  initialOptions: HeatmapOverlayOptions = {},
): HeatmapOverlay => {
  if (!maps?.OverlayView) {
    throw new Error('createHeatmapOverlay: google.maps.OverlayView not available.');
  }

  class HeatmapCanvasOverlay extends maps.OverlayView {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D | null;
    private points: HeatmapPoint[];
    private radius: number;
    private opacity: number;
    private maxIntensity: number;
    private palette: Uint8ClampedArray;
    private isAdded: boolean;

    constructor(opts: HeatmapOverlayOptions) {
      super();
      this.canvas = document.createElement('canvas');
      this.canvas.style.position = 'absolute';
      this.canvas.style.pointerEvents = 'none';
      this.canvas.style.willChange = 'transform';
      this.ctx = this.canvas.getContext('2d');
      this.points = [];
      this.radius = opts.radius ?? 40;
      this.opacity = opts.opacity ?? 0.75;
      this.maxIntensity = opts.maxIntensity ?? 1;
      this.palette = buildPalette(opts.gradient ?? DEFAULT_GRADIENT);
      this.canvas.style.opacity = String(this.opacity);
      this.isAdded = false;
    }

    /** OverlayView lifecycle — attach the canvas to the overlay pane. */
    onAdd() {
      const panes = this.getPanes();
      if (panes?.overlayLayer) {
        panes.overlayLayer.appendChild(this.canvas);
        this.isAdded = true;
      }
    }

    onRemove() {
      if (this.canvas.parentNode) {
        this.canvas.parentNode.removeChild(this.canvas);
      }
      this.isAdded = false;
    }

    /**
     * Reproject points and redraw the canvas. Called by Maps on every
     * pan / zoom / resize, and explicitly by us when data changes.
     */
    draw() {
      const proj = this.getProjection();
      const ctx = this.ctx;
      if (!proj || !ctx) return;

      if (this.points.length === 0) {
        this.canvas.width = 0;
        this.canvas.height = 0;
        return;
      }

      // Project every point into the OverlayLayer pane's pixel coords,
      // then size the canvas to its bounding box (plus radius padding).
      // Anchoring the canvas at minX/minY keeps the canvas itself small
      // while still aligning blobs with their points after pan/zoom.
      const pad = this.radius;
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      const projected: Array<{ x: number; y: number; weight: number }> = [];

      for (const p of this.points) {
        const ll = resolveLatLng(p);
        if (!ll) continue;
        const px = proj.fromLatLngToDivPixel(new maps.LatLng(ll.lat, ll.lng));
        if (!px) continue;
        const w = typeof p.weight === 'number' ? p.weight : 1;
        if (!Number.isFinite(w) || w <= 0) continue;
        projected.push({ x: px.x, y: px.y, weight: w });
        if (px.x < minX) minX = px.x;
        if (px.y < minY) minY = px.y;
        if (px.x > maxX) maxX = px.x;
        if (px.y > maxY) maxY = px.y;
      }

      if (projected.length === 0) {
        this.canvas.width = 0;
        this.canvas.height = 0;
        return;
      }

      minX -= pad;
      minY -= pad;
      maxX += pad;
      maxY += pad;
      const width = Math.max(1, Math.ceil(maxX - minX));
      const height = Math.max(1, Math.ceil(maxY - minY));

      this.canvas.style.left = `${minX}px`;
      this.canvas.style.top = `${minY}px`;
      this.canvas.width = width;
      this.canvas.height = height;
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${height}px`;

      // Pass 1 — accumulate radial gradients. `lighter` (a.k.a. PLUS)
      // adds RGB+alpha from each blob, so overlapping points produce
      // higher alpha and thus a hotter palette index in pass 2.
      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'lighter';
      for (const pt of projected) {
        const x = pt.x - minX;
        const y = pt.y - minY;
        const intensity = Math.min(1, pt.weight / Math.max(1e-6, this.maxIntensity));
        if (intensity <= 0) continue;
        const grad = ctx.createRadialGradient(x, y, 0, x, y, this.radius);
        grad.addColorStop(0, `rgba(255, 255, 255, ${intensity})`);
        grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = grad;
        ctx.fillRect(x - this.radius, y - this.radius, this.radius * 2, this.radius * 2);
      }

      // Pass 2 — colorize: replace each pixel's RGB based on its alpha
      // using the palette LUT. Alpha is preserved so transparent
      // background stays transparent.
      const img = ctx.getImageData(0, 0, width, height);
      const data = img.data;
      const palette = this.palette;
      for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a === 0) continue;
        const offset = a * 4;
        data[i] = palette[offset];
        data[i + 1] = palette[offset + 1];
        data[i + 2] = palette[offset + 2];
      }
      ctx.putImageData(img, 0, 0);
    }

    setData(
      points: HeatmapPoint[],
      opts?: { maxIntensity?: number; radius?: number },
    ) {
      this.points = points || [];
      if (opts?.maxIntensity != null && Number.isFinite(opts.maxIntensity)) {
        this.maxIntensity = opts.maxIntensity;
      }
      if (opts?.radius != null && opts.radius > 0) {
        this.radius = opts.radius;
      }
      if (this.isAdded) this.draw();
    }

    setOptions(opts: Partial<HeatmapOverlayOptions>) {
      if (opts.radius != null) this.radius = opts.radius;
      if (opts.opacity != null) {
        this.opacity = opts.opacity;
        this.canvas.style.opacity = String(this.opacity);
      }
      if (opts.maxIntensity != null) this.maxIntensity = opts.maxIntensity;
      if (opts.gradient) this.palette = buildPalette(opts.gradient);
      if (this.isAdded) this.draw();
    }

    redraw() {
      if (this.isAdded) this.draw();
    }
  }

  return new HeatmapCanvasOverlay(initialOptions) as unknown as HeatmapOverlay;
};
